import React from 'react';
import { X, FileText, BarChart3, CheckCircle, AlertCircle } from 'lucide-react';

// Helper to extract value from new bounding box format or legacy format
function getValue<T>(field: T | { value: T } | undefined | null, defaultValue: T): T {
  if (field === undefined || field === null) {
    return defaultValue;
  }

  // Check if it's the new format with bounding_box
  if (typeof field === 'object' && field !== null && 'value' in field) {
    const value = (field as { value: T }).value;
    return value !== null && value !== undefined ? value : defaultValue;
  }

  // Legacy format - return as-is
  return field as T;
}

interface OCRResult {
  extracted_text: string;
  ocr_confidence: number;
  line_count: number;
  lines: Array<{
    text: string;
    confidence: number;
    word_count?: number;
  }>;
  processing_metadata: {
    processing_time_seconds: number;
    file_size_bytes: number;
    file_path: string;
    processing_mode: string;
    timestamp: string;
  };
  raw_response: {
    mode: string;
    file_name: string;
    file_size: number;
    note: string;
  };
}

interface HybridProcessingResult {
  file_path: string;
  processing_status: string;
  processing_time_seconds: number;
  timestamp: string;
  ocr_result: {
    extracted_text: string;
    ocr_confidence: number;
    line_count: number;
    processing_time: number;
  };
  llm_result: {
    structured_data: {
      payroll_period: {
        begin_date:
          | string
          | {
              value: string;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        end_date:
          | string
          | {
              value: string;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        check_date:
          | string
          | {
              value: string;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        pay_period:
          | number
          | {
              value: number;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
      };
      company: {
        name:
          | string
          | {
              value: string;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        client_id:
          | string
          | {
              value: string;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        pay_group:
          | string
          | {
              value: string;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
      };
      employees: Array<{
        name:
          | string
          | {
              value: string;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        employee_id:
          | string
          | {
              value: string;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        employee_type?: 'hourly' | 'salary' | { value: 'hourly' | 'salary' };
        hourly_rate:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        salary_amount?:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        regular_hours:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        overtime_hours:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        bonus_earnings:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        sick_hours:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        vacation_hours:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        holiday_hours:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        other_hours:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
        other_earnings:
          | number
          | null
          | {
              value: number | null;
              bounding_box?: number[];
              page_number?: number;
              confidence?: number;
            };
      }>;
      totals: {
        regular_hours: number;
        overtime_hours: number;
        bonus_earnings: number;
        other_earnings: number;
      };
    };
    llm_confidence: number;
    extraction_method: string;
    processing_time: number;
  };
  hybrid_confidence: number;
  processing_method: string;
  processing_metadata: {
    total_processing_time: number;
    ocr_processing_time: number;
    llm_processing_time: number;
    file_size_bytes: number;
    ocr_mode: string;
    llm_mode: string;
  };
}

interface StatusInfo {
  status: string;
  stage: string;
  display_status?: string;
  display_stage?: string;
  processing_status?: string;
  current_stage?: string;
  attachment_name?: string;
  error: string | null;
  processing_started_at: string | null;
  processing_completed_at: string | null;
  can_retry: boolean;
}

interface OCRResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: OCRResult | HybridProcessingResult | null;
  isLoading: boolean;
  error: string | null;
  statusInfo?: StatusInfo | null;
  onRetry?: () => void;
}

const OCRResultsModal: React.FC<OCRResultsModalProps> = ({
  isOpen,
  onClose,
  result,
  isLoading,
  error,
  statusInfo,
  onRetry,
}) => {
  if (!isOpen) return null;

  // Check if this is a hybrid processing result
  const isHybridResult = result && 'hybrid_confidence' in result;

  // Helper function to get status color
  const getStatusColor = (status: string, stage: string) => {
    // Check both status and processing_status fields
    const actualStatus = statusInfo?.processing_status || status;
    if (actualStatus === 'completed')
      return 'bg-green-100 text-green-800 border-green-200';
    if (actualStatus === 'failed') return 'bg-red-100 text-red-800 border-red-200';
    if (actualStatus === 'queued')
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    if (actualStatus === 'processing') {
      if (stage === 'ocr_extraction')
        return 'bg-blue-100 text-blue-800 border-blue-200';
      if (stage === 'llm_extraction')
        return 'bg-purple-100 text-purple-800 border-purple-200';
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };
  const hybridResult = isHybridResult ? (result as HybridProcessingResult) : null;
  const ocrResult = !isHybridResult ? (result as OCRResult) : null;

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 bg-green-50';
    if (confidence >= 0.7) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.9) return 'High';
    if (confidence >= 0.7) return 'Medium';
    return 'Low';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-neutral-900">
              {isHybridResult
                ? 'Hybrid OCR+LLM Processing Results'
                : 'OCR Processing Results'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Status Display Section */}
        {statusInfo && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              Processing Status
            </h3>

            {/* Current Status Box */}
            <div
              className={`inline-block px-4 py-2 rounded-lg border-2 font-medium ${getStatusColor(statusInfo.status, statusInfo.stage)}`}
            >
              {statusInfo.display_status || statusInfo.processing_status || 'Unknown'}
            </div>

            {/* Stage Information - Horizontal Layout */}
            <div className="mt-3 text-sm text-gray-600 flex flex-wrap gap-4">
              <div>
                <strong>Stage:</strong>{' '}
                {statusInfo.display_stage || statusInfo.current_stage || 'N/A'}
              </div>
              {statusInfo.processing_started_at && (
                <div>
                  <strong>Started:</strong>{' '}
                  {new Date(statusInfo.processing_started_at).toLocaleString()}
                </div>
              )}
              {statusInfo.processing_completed_at && (
                <div>
                  <strong>Completed:</strong>{' '}
                  {new Date(statusInfo.processing_completed_at).toLocaleString()}
                </div>
              )}
            </div>

            {/* Spinner Below Status */}
            {statusInfo.status === 'processing' && (
              <div className="mt-4 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Error Details */}
            {statusInfo.error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-red-800 font-medium">Error Details:</div>
                <div className="text-red-700 text-sm mt-1">{statusInfo.error}</div>
              </div>
            )}

            {/* Retry Button */}
            {statusInfo.can_retry && onRetry && (
              <div className="mt-3">
                <button
                  onClick={onRetry}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry Processing
                </button>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:min-h-[80px] [&::-webkit-scrollbar-thumb:hover]:bg-gray-500">
          {/* Show spinner only when loading and no status info yet */}
          {isLoading && !statusInfo && !result && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="text-lg text-neutral-600">
                  Starting document processing...
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-800 font-medium">
                  Hybrid Processing Error
                </span>
              </div>
              <p className="text-red-700 mt-2">{error}</p>
            </div>
          )}

          {result && (
            <div className="space-y-6">
              {/* Processing Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-800">Processing Complete</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-blue-600 font-medium">Processing Mode:</span>
                    <p className="text-blue-800">
                      {isHybridResult
                        ? hybridResult?.processing_method || 'Hybrid OCR+LLM'
                        : ocrResult?.processing_metadata?.processing_mode || 'OCR'}
                    </p>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">Processing Time:</span>
                    <p className="text-blue-800">
                      {(() => {
                        // Calculate from timestamps if available
                        if (
                          statusInfo?.processing_started_at &&
                          statusInfo?.processing_completed_at
                        ) {
                          const start = new Date(
                            statusInfo.processing_started_at
                          ).getTime();
                          const end = new Date(
                            statusInfo.processing_completed_at
                          ).getTime();
                          const seconds = ((end - start) / 1000).toFixed(2);
                          return `${seconds}s`;
                        }
                        // Fallback to result data
                        if (isHybridResult) {
                          return `${hybridResult?.processing_time_seconds?.toFixed(2) || 'N/A'}s`;
                        }
                        return `${ocrResult?.processing_metadata?.processing_time_seconds?.toFixed(2) || 'N/A'}s`;
                      })()}
                    </p>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">File:</span>
                    <p className="text-blue-800">
                      {(() => {
                        // Try to get filename from various sources
                        if (statusInfo?.attachment_name)
                          return statusInfo.attachment_name;
                        if (isHybridResult) {
                          return (
                            hybridResult?.file_path?.split(/[/\\]/).pop() || 'Unknown'
                          );
                        }
                        return ocrResult?.raw_response?.file_name || 'Unknown';
                      })()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Confidence Summary */}
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-neutral-800">
                    Confidence Analysis
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {isHybridResult ? (
                    <>
                      <div className="text-center">
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(hybridResult?.ocr_result?.ocr_confidence || 0)}`}
                        >
                          OCR Confidence
                        </div>
                        <p className="text-2xl font-bold text-neutral-900 mt-2">
                          {(
                            (hybridResult?.ocr_result?.ocr_confidence || 0) * 100
                          ).toFixed(1)}
                          %
                        </p>
                      </div>
                      <div className="text-center">
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(hybridResult?.llm_result?.llm_confidence || 0)}`}
                        >
                          LLM Confidence
                        </div>
                        <p className="text-2xl font-bold text-neutral-900 mt-2">
                          {(
                            (hybridResult?.llm_result?.llm_confidence || 0) * 100
                          ).toFixed(1)}
                          %
                        </p>
                      </div>
                      <div className="text-center">
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(hybridResult?.hybrid_confidence || 0)}`}
                        >
                          Hybrid Confidence
                        </div>
                        <p className="text-2xl font-bold text-neutral-900 mt-2">
                          {((hybridResult?.hybrid_confidence || 0) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-center">
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(ocrResult?.ocr_confidence || 0)}`}
                        >
                          {getConfidenceLabel(ocrResult?.ocr_confidence || 0)}{' '}
                          Confidence
                        </div>
                        <p className="text-2xl font-bold text-neutral-900 mt-2">
                          {((ocrResult?.ocr_confidence || 0) * 100).toFixed(1)}%
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="text-neutral-600 text-sm">Lines Processed</div>
                        <p className="text-2xl font-bold text-neutral-900 mt-2">
                          {ocrResult?.line_count || 0}
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="text-neutral-600 text-sm">Processing Mode</div>
                        <p className="text-lg font-medium text-neutral-900 mt-2 capitalize">
                          {ocrResult?.processing_metadata?.processing_mode?.replace(
                            '_',
                            ' '
                          ) || 'Unknown'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Structured Data (Hybrid Results Only) */}
              {isHybridResult && hybridResult?.llm_result?.structured_data && (
                <div className="bg-white border border-neutral-200 rounded-lg">
                  <div className="p-4 border-b border-neutral-200">
                    <h3 className="font-medium text-neutral-900">
                      Structured Payroll Data
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Data extracted and structured by LLM
                    </p>
                  </div>
                  <div className="p-4">
                    <div className="space-y-4">
                      {/* Company Info */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <h4 className="font-medium text-green-800 mb-2">
                          Company Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="font-medium">Name:</span>{' '}
                            {getValue(
                              hybridResult.llm_result.structured_data.company.name,
                              'N/A'
                            )}
                          </div>
                          <div>
                            <span className="font-medium">Client ID:</span>{' '}
                            {getValue(
                              hybridResult.llm_result.structured_data.company.client_id,
                              'N/A'
                            )}
                          </div>
                          <div>
                            <span className="font-medium">Pay Group:</span>{' '}
                            {getValue(
                              hybridResult.llm_result.structured_data.company.pay_group,
                              'N/A'
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Payroll Period */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="font-medium text-blue-800 mb-2">
                          Payroll Period
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="font-medium">Begin Date:</span>{' '}
                            {getValue(
                              hybridResult.llm_result.structured_data.payroll_period
                                .begin_date,
                              'N/A'
                            )}
                          </div>
                          <div>
                            <span className="font-medium">End Date:</span>{' '}
                            {getValue(
                              hybridResult.llm_result.structured_data.payroll_period
                                .end_date,
                              'N/A'
                            )}
                          </div>
                          <div>
                            <span className="font-medium">Check Date:</span>{' '}
                            {getValue(
                              hybridResult.llm_result.structured_data.payroll_period
                                .check_date,
                              'N/A'
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Employees */}
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <h4 className="font-medium text-purple-800 mb-2">
                          Employees (
                          {hybridResult.llm_result.structured_data.employees.length})
                        </h4>
                        <div className="space-y-2">
                          {hybridResult.llm_result.structured_data.employees.map(
                            (emp, index) => {
                              // Extract values using helper (handles both bbox format and legacy format)
                              const employeeType = getValue(
                                emp.employee_type,
                                'hourly'
                              );
                              const isSalaried = employeeType === 'salary';
                              const name = getValue(emp.name, 'N/A');
                              const employeeId = getValue(emp.employee_id, 'N/A');
                              const salaryAmount = getValue(emp.salary_amount, 0);
                              const hourlyRate = getValue(emp.hourly_rate, 0);
                              const regularHours = getValue(emp.regular_hours, 0);
                              const overtimeHours = getValue(emp.overtime_hours, 0);
                              const bonusEarnings = getValue(emp.bonus_earnings, 0);

                              return (
                                <div
                                  key={index}
                                  className="bg-white border border-purple-200 rounded p-2 text-sm"
                                >
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">Name:</span>
                                      <span>{name}</span>
                                      {isSalaried && (
                                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                          SALARY
                                        </span>
                                      )}
                                    </div>
                                    <div>
                                      <span className="font-medium">ID:</span>{' '}
                                      {employeeId}
                                    </div>

                                    {isSalaried ? (
                                      <>
                                        <div>
                                          <span className="font-medium">Salary:</span> $
                                          {(salaryAmount || 0).toFixed(2)}
                                        </div>
                                        <div>
                                          <span className="font-medium">Type:</span>{' '}
                                          <span className="text-blue-600">
                                            Salaried
                                          </span>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div>
                                          <span className="font-medium">Rate:</span> $
                                          {(hourlyRate || 0).toFixed(2)}
                                        </div>
                                        <div>
                                          <span className="font-medium">Hours:</span>{' '}
                                          {regularHours || 0}
                                        </div>
                                      </>
                                    )}

                                    <div>
                                      <span className="font-medium">Overtime:</span>{' '}
                                      {overtimeHours || 0}
                                    </div>
                                    <div>
                                      <span className="font-medium">Bonus:</span> $
                                      {(bonusEarnings || 0).toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* OCR Extracted Text */}
              <div className="bg-white border border-neutral-200 rounded-lg">
                <div className="p-4 border-b border-neutral-200">
                  <h3 className="font-medium text-neutral-900">OCR Extracted Text</h3>
                </div>
                <div className="p-4">
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap text-sm text-neutral-800 font-mono">
                      {isHybridResult
                        ? hybridResult?.ocr_result?.extracted_text ||
                          'No text extracted'
                        : ocrResult?.extracted_text || 'No text extracted'}
                    </pre>
                  </div>
                </div>
              </div>

              {/* Line-by-Line Analysis (OCR Results Only) */}
              {!isHybridResult && ocrResult?.lines && (
                <div className="bg-white border border-neutral-200 rounded-lg">
                  <div className="p-4 border-b border-neutral-200">
                    <h3 className="font-medium text-neutral-900">
                      Line-by-Line Analysis
                    </h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {ocrResult.lines.map((line, index) => (
                      <div
                        key={index}
                        className="p-3 border-b border-neutral-100 last:border-b-0"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 mr-4">
                            <p className="text-sm text-neutral-800">{line.text}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div
                              className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceColor(line.confidence)}`}
                            >
                              {(line.confidence * 100).toFixed(1)}%
                            </div>
                            {line.word_count && (
                              <span className="text-xs text-neutral-500">
                                {line.word_count} words
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-neutral-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-neutral-600 hover:text-neutral-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(OCRResultsModal);
